import axios from '@axios'

const state = () => ({
  tickets: {
    data: [],
    meta: {
      pagination: {
        page: 1,
        per_page: 10,
        total_pages: 1,
        total_objects: 0,
      },
    },
  },
  ticket: null,
  loading: false,
})

const getters = {
  tickets: s => s.tickets.data,
  ticketsPagination: s => s.tickets?.meta?.pagination || {},
  ticket: s => s.ticket,
  loading: s => s.loading,
}

const mutations = {
  SET_LOADING(state, payload) {
    state.loading = payload
  },
  SET_TICKETS(state, payload) {
    state.tickets = payload || { data: [], meta: { pagination: {} } }
  },
  SET_TICKET(state, payload) {
    state.ticket = payload || null
  },
}

const normalizeTicketPayload = ticket => {
  const payload = { ...ticket }
  const images = Array.isArray(payload.images)
    ? payload.images.filter(file => file instanceof File)
    : []
  const removeImageIds = Array.isArray(payload.remove_image_ids)
    ? payload.remove_image_ids.filter(id => id !== null && id !== undefined && String(id).length > 0)
    : []

  delete payload.images
  delete payload.remove_image_ids

  const hasFileChanges = images.length > 0 || removeImageIds.length > 0
  if (!hasFileChanges) return { body: { ticket: payload }, isMultipart: false }

  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    formData.append(`ticket[${key}]`, value)
  })

  images.forEach(file => {
    formData.append('ticket[images][]', file, file.name)
  })

  removeImageIds.forEach(id => {
    formData.append('ticket[remove_image_ids][]', id)
  })

  return { body: formData, isMultipart: true }
}

const performTicketUpdate = async ({ id, ticket }) => {
  const { body, isMultipart } = normalizeTicketPayload(ticket)
  const config = isMultipart
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {}

  const { data } = await axios.put(`/v1/tickets/${id}`, body, config)
  return data
}

const actions = {
  async fetchTickets({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const { data } = await axios.get('/v1/tickets', { params })
      commit('SET_TICKETS', data)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTicket({ commit }, ticketId) {
    commit('SET_LOADING', true)
    try {
      const { data } = await axios.get(`/v1/tickets/${ticketId}`)
      commit('SET_TICKET', data)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createTicket({ commit }, ticket) {
    commit('SET_LOADING', true)
    try {
      const { body, isMultipart } = normalizeTicketPayload(ticket)
      const config = isMultipart
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {}

      const { data } = await axios.post('/v1/tickets', body, config)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTicket({ commit }, payload) {
    commit('SET_LOADING', true)
    try {
      const data = await performTicketUpdate(payload)
      commit('SET_TICKET', data)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTicketInline({ commit }, payload) {
    const data = await performTicketUpdate(payload)
    commit('SET_TICKET', data)
    return data
  },

  async refineTicketWithAi({ commit }, id) {
    commit('SET_LOADING', true)
    try {
      const { data } = await axios.post(`/v1/tickets/${id}/refine_with_ai`)
      commit('SET_TICKET', data?.data || null)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createGitlabIssue({ commit }, id) {
    commit('SET_LOADING', true)
    try {
      const { data } = await axios.post(`/v1/tickets/${id}/create_gitlab_issue`)
      commit('SET_TICKET', data?.data || null)
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
